import { Component, type ErrorInfo, type ReactNode } from "react";

// A render-time safety net (BRK-S259, and the component BRK-S032 calls for):
// when something below it throws while rendering, it shows its fallback
// instead of letting React unmount the whole page to a blank screen (NN #7).
// A class component because React offers error boundaries no other way; it is
// the one sanctioned exception to the function-component idiom.
//
// The error is logged with console.error only: the SPA ships no telemetry.
// `resetKeys` clears the error when any of them changes, so new data (another
// page, a reload) gets a fresh try without the caller wiring a reset.

interface ErrorBoundaryProps {
  children: ReactNode;
  /** What renders in the children's place once one of them throws. */
  fallback: (props: { error: Error; reset: () => void }) => ReactNode;
  /** A change in any of these clears a caught error. */
  resetKeys?: readonly unknown[];
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("A view failed to render:", error, info.componentStack);
  }

  componentDidUpdate(previous: ErrorBoundaryProps) {
    if (
      this.state.error &&
      changed(previous.resetKeys ?? [], this.props.resetKeys ?? [])
    ) {
      this.reset();
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    return error
      ? this.props.fallback({ error, reset: this.reset })
      : this.props.children;
  }
}

function changed(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length !== b.length || a.some((value, i) => !Object.is(value, b[i]));
}
